import React, {useState, useEffect} from "react";
import {Autocomplete, Box, Button, Grid, IconButton, TextField, Typography} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import {useLocation, useNavigate} from "react-router-dom";
import Container from "../../../common/Container";
import {useSnack} from "../../../utils/useSnack";
import {useSelector} from "react-redux";
import {State} from "../../../redux/store";
import {Participant} from "../../../types/participant";
import {Auction} from "../../../types/auction";
import {auctionService} from "../../../api/services/auction";
import {Company} from "../../../types/company";
import ClearIcon from '@mui/icons-material/Clear';
import {useTranslation} from "react-i18next";
import {AuctionState, AuctionType, LotState} from "../../../utils/enums";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import {Column, columnStyle, largeColumnStyle, CompanyTitleColumn, GrossColumn} from "../../../common/Columns";
import BoxWithLabel from "../../../common/BoxWithLabel";
import {useDictionary} from "../../../utils/useDictionary";

export default function Auctions({
                                     title, auctionPath, auctionsEndpoint, auctionType
                                 }: {
    title: string;
    auctionPath: string;
    auctionsEndpoint: string;
    auctionType?: AuctionType;
}) {
    const {t} = useTranslation();
    const {snack} = useSnack();
    const location = useLocation();
    const navigate = useNavigate();
    const state = useSelector((state: State) => state);
    const company: Company | null = state.company.company;

    function onCreateAuction() {
        navigate(`/account-${auctionPath}/create`);
    }

    function goToAuctionDetails(id: string, participant: Participant | null) {
        if (participant) {
            navigate(`/account-auctions-participated/${id}`);
            return
        }
        navigate(`/account-${auctionPath}/${id}`)
    }

    const [auctions, setAuctions] = useState<Auction[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalElements, setTotalElements] = useState(0);

    async function getAuctions(text: string) {
        if (!company) {
            return
        }

        try {
            setLoading(true);
            const response = await auctionService
                .getAuctions(company.id, text, page, pageSize, auctionsEndpoint, auctionType, [auctionState.selected?.value || "" as AuctionState]);

            if (response.status === "success" && response.data) {
                setAuctions(response.data.data);
                setTotalElements(response.data.totalElements);
            } else {
                snack("Ошибка при получении аукционов", "error");
            }
        } catch (err) {
            snack("Ошибка при получении аукционов", "error");
        } finally {
            setLoading(false)
        }
    }

    let enumDict: any = AuctionState;

    if(auctionsEndpoint === 'participated') {
        enumDict = {
            published: AuctionState.published,
            registration: AuctionState.registration,
            opened: AuctionState.opened,
            review: AuctionState.review,
            canceled: AuctionState.canceled,
            closed: AuctionState.closed,
            finished: AuctionState.finished,
            issued: AuctionState.issued
        };
    } else if (auctionsEndpoint === 'actual') {
        enumDict  = {
            published: AuctionState.published,
            registration: AuctionState.registration,
            opened: AuctionState.opened
        };
    }

    const auctionState = useDictionary(
        enumDict,
        (type) => ({value: type, label: t(`auction_state.${type}`)}),
        {value: '', label: ''}
    );


    const emptyOption = {value: "", label: ""};
    const dictionaryWithEmpty = [emptyOption, ...auctionState.dictionary];

    const handleSearch = (text: string) => getAuctions(text);
    const handleClear = () => {
        setSearchText("");
        auctionState.setSelected({'value': '', 'label': ''})
        getAuctions("")
    }

    const onPageChange = (newPage: number) => setPage(newPage);
    const onPageSizeChange = (newSize: number) => {
        setPage(0);
        setPageSize(newSize)
    };

    useEffect(() => {
        if (company) getAuctions(searchText);
    }, [company, location.pathname, page, pageSize]);

    const isActualOrParticipated = ["actual", "participated"].includes(auctionsEndpoint);

    function chooseDictionaryFields() {
        if (isActualOrParticipated) return [
            Column("type", 150, columnStyle, "auction_type"),
            Column("state", 120, columnStyle, "auction_state"),
        ]
        else return [
            Column("state", 120, columnStyle, "auction_state"),
        ];
    }

    return (
        <Box>
            <Container paddingY={{xs: 2, sm: 4}} maxWidth="unset">
                <Box marginBottom={4}>
                    <Typography
                        variant="h3"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                        }}
                    >
                        {title}
                    </Typography>
                </Box>

                <Grid container>
                    <Grid item xs={12}>
                        <BoxWithLabel label={t("common.filters")}>
                            <Grid container marginBottom={2} spacing={2} alignItems="center">
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <TextField fullWidth color="primary" variant="outlined" size="small"
                                               label={t("auctions_page.search_auction")}
                                               value={searchText} onChange={e => setSearchText(e.target.value)}
                                               onKeyPress={e => {
                                                   if (e.key === "Enter") {
                                                       handleSearch(searchText)
                                                   }
                                               }}
                                               InputProps={{
                                                   endAdornment: searchText ? (
                                                       <IconButton size="small" onClick={handleClear}>
                                                           <ClearIcon/>
                                                       </IconButton>
                                                   ) : undefined
                                               }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={8} lg={8}>
                                    <Autocomplete
                                        disablePortal size="small" sx={{mb: .5}}
                                        options={dictionaryWithEmpty}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        value={auctionState.selected}
                                        onChange={(e, value) => {
                                            if (!!value) {
                                                auctionState.onSelectChange(e, value)
                                            } else {
                                                auctionState.onSelectChange(e, emptyOption)
                                            }
                                        }}
                                        fullWidth
                                        renderInput={params =>
                                            <TextField {...params} fullWidth color="primary"
                                                       name="lotState" label={t("cabinet.lots.filter.status")}
                                            />
                                        }
                                    />
                                </Grid>
                                {/* Кнопка для поиска */}
                                <Grid item xs={12} sm={2} md={2} lg={2}>
                                    <Button
                                        fullWidth
                                        color="primary"
                                        variant="contained"
                                        onClick={(e) => handleSearch(searchText)}
                                        size={'small'}
                                    >
                                        {t("common.search")}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={2} md={2} lg={2}>
                                    <Button
                                        fullWidth
                                        color="secondary"
                                        variant="contained"
                                        onClick={handleClear}
                                        size={'small'}
                                    >
                                        {t("common.clear")}
                                    </Button>
                                </Grid>
                            </Grid>
                        </BoxWithLabel>
                    </Grid>
                </Grid>
                {auctionType
                    ? <Grid item xs={12} sm={5} md={3} lg={2}>
                        <Button color="primary" variant="contained"
                                onClick={onCreateAuction}>
                            <span style={{whiteSpace: 'nowrap'}}>{t("auctions_page.create")}</span>
                        </Button>
                    </Grid>
                    : null
                }

                <Box sx={{
                    paddingTop: 2,
                }}>
                    <DataGrid
                        components={{NoRowsOverlay: () => <NoRowsOverlay/>}}
                        sx={{
                            height: 600,
                            "& .MuiDataGrid-cell": {
                                whiteSpace: "pre-wrap"
                            },
                        }}
                        columnBuffer={isActualOrParticipated ? 8 : 7} // Columns length
                        rows={auctions}
                        columns={[
                            ...chooseDictionaryFields(),
                            Column("auctionNo", 140, {...columnStyle, ...largeColumnStyle}),
                            Column("title", 165, {...columnStyle, ...largeColumnStyle}),
                            Column("productType", 120, columnStyle, "product_type"),
                            Column("description", 165, {...columnStyle, ...largeColumnStyle}),
                            Column("companyName", 165, columnStyle),
                            GrossColumn(200, columnStyle),
                        ]}
                        onRowClick={(rowData) => goToAuctionDetails(rowData.row.id, rowData.row.participant)}
                        page={page} onPageChange={onPageChange}
                        pageSize={pageSize} onPageSizeChange={onPageSizeChange}
                        rowCount={totalElements} rowsPerPageOptions={[4, 8, 16]}
                        loading={loading} paginationMode="server"
                        density="comfortable" style={{whiteSpace: "pre-wrap"}}
                    />
                </Box>
            </Container>
        </Box>
    )
}
